import { IconAlertCircle } from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import type { KeyOutageOutage } from '../lib/key-outage';
import { since } from '../lib/time';
import { Select } from './Select';
import { Button } from './ui/Button';

// The account-wide key-outage banner (epic #5099, slice #5119): what is held,
// by step, and the picker when the server named a key a pick may resolve. The
// server decides which labels a pick may name (`pick_labels`); a second copy of
// the rule in this file could offer a label the server then refuses, so the
// render iterates `pick_labels` verbatim and nothing else.
//
// Task steps reuse `account_keys.routes.purpose.<step>` — this is the only
// remaining renderer of that family — so a purpose gaining a member is
// picked up here by the same `i18n.test.ts` assertion that already guards it.
// The one step outside that vocabulary, an ask's `triage`, gets its own
// `key_outage.step_triage` label.
//
// Each unit pluralizes INDEPENDENTLY (`{n} task, {n} ask` vs `{n} tasks, {n}
// asks`), so the one-line total reads right whether `tasks` and `asks` both
// equal 1, neither does, or just one does. The four combinations get four
// keys; a single "singular/plural" toggle would have rendered "1 tasks" or
// "0 tasks, 1 asks" — both measured defects in the prior copy.
//
// The title and the reasons line read the outage's `held` rows: a judge-only
// outage (every row held because the AI judge calls fail — keys are fine) says
// so instead of asserting key exhaustion, and the reasons line names every
// distinct `hold_reason`. Those keys are rendered through a DYNAMIC id, so a
// hold-reason member without a label in `en.json` ships `⟦…⟧` — the gap
// `i18n.test.ts`'s key-outage assertion exists to catch.

/** The pick's control id. The banner is account-wide and mounts once, so one id is enough. */
const PICK_ID = 'key-outage-pick';

export type KeyOutageNoticeProps = {
  /** The open outage, exactly as the API returned it. */
  readonly outage: KeyOutageOutage;
  /** The label the operator picked; `null` until they choose. */
  readonly pickedLabel: string | null;
  /** True while the pick POST is in flight — the control is held. */
  readonly picking: boolean;
  /** Server `detail` from a refused pick; `null` otherwise. */
  readonly pickError: string | null;
  /** Fires on every select change; the parent owns the label state. */
  readonly onChange: (label: string | null) => void;
  /** Fires on submit; receives the chosen label, never null. */
  readonly onSubmit: (label: string) => void;
};

/** The label a `step` renders as — its i18n key. */
function stepLabelKey(step: string): string {
  return step === 'triage' ? 'key_outage.step_triage' : `account_keys.routes.purpose.${step}`;
}

/** Total of `tasks` and `asks` across every held step — the one-line headline. */
function totalHeld(outage: KeyOutageOutage): { readonly tasks: number; readonly asks: number } {
  let tasks = 0;
  let asks = 0;
  for (const step of outage.held_by_step) {
    tasks += step.tasks;
    asks += step.asks;
  }
  return { tasks, asks };
}

/** Distinct `hold_reason`s across the held rows, in first-appearance order. */
function heldReasons(outage: KeyOutageOutage): readonly string[] {
  const reasons: string[] = [];
  for (const item of outage.held) {
    if (!reasons.includes(item.hold_reason)) reasons.push(item.hold_reason);
  }
  return reasons;
}

/** The judge headline when every held row is a failing judge call, the key headline otherwise. An outage whose held rows arrived empty keeps the key headline rather than claiming a judge fault. */
function titleKey(reasons: readonly string[]): string {
  const judgeOnly = reasons.length > 0 && reasons.every((reason) => reason === 'model_unavailable');
  return judgeOnly ? 'key_outage.title_judge' : 'key_outage.title';
}

/** The one reasons line: the label lead-in, then each reason's label, joined. */
function reasonsLine(reasons: readonly string[]): string {
  return reasons.map((reason) => t(`key_outage.reason_${reason}`)).join(', ');
}

/**
 * "3 tasks, 1 ask": each noun takes its plural from its OWN count, so the line
 * composes two plural families instead of enumerating the four combinations
 * (which is what `_singular`/`_tasks_singular`/`_asks_singular`/`_plural` did).
 */
function heldCounts(tasks: number, asks: number): { tasks: string; asks: string } {
  return { tasks: tPlural('key_outage.tasks', tasks), asks: tPlural('key_outage.asks', asks) };
}

export function KeyOutageNotice(props: KeyOutageNoticeProps): JSX.Element {
  const totals = totalHeld(props.outage);
  const reasons = heldReasons(props.outage);
  return (
    <div class="alert alert--icon alert--destructive" role="status">
      <IconAlertCircle />
      <p class="alert__title">{t(titleKey(reasons))}</p>
      <p class="alert__desc">{t('key_outage.summary', heldCounts(totals.tasks, totals.asks))}</p>
      {/* `held` may arrive empty (the read schema admits it): no reason to
          name, so no reasons line — the same empty rule the title keeps. */}
      <Show when={reasons.length > 0}>
        <p class="alert__desc">
          {t('key_outage.reasons_label')}: {reasonsLine(reasons)}
        </p>
      </Show>
      <p class="alert__desc">{t('key_outage.opened', { when: since(props.outage.opened_at) })}</p>
      {/* Expandable per-step breakdown: the one-line total stays visible
          (the answer to "how bad is it"), the list is the answer to
          "where". A `<details>` keeps the disclosure keyboard-accessible
          and screen-reader-friendly without bespoke state. */}
      <details class="key-outage__details">
        <summary class="key-outage__summary">{t('key_outage.step_list_label')}</summary>
        <ul class="key-outage__steps">
          {props.outage.held_by_step.map((step) => (
            <li class="key-outage__step">
              <span class="key-outage__step-label">{t(stepLabelKey(step.step))}</span>
              <span class="key-outage__step-count">
                {t('key_outage.step_counts', heldCounts(step.tasks, step.asks))}
              </span>
            </li>
          ))}
        </ul>
      </details>
      <Show
        when={props.outage.pick_labels.length > 0}
        fallback={<p class="alert__desc">{t('key_outage.no_pick')}</p>}
      >
        <p class="alert__desc">{t('key_outage.pick_label')}</p>
        <div class="key-outage__pick">
          <label id={`${PICK_ID}-label`} for={PICK_ID} class="sr-only">
            {t('key_outage.pick_label')}
          </label>
          <Select
            a11y={{ id: PICK_ID }}
            label={t('key_outage.pick_label')}
            value={props.pickedLabel ?? ''}
            disabled={props.picking}
            options={[
              { value: '', label: t('key_outage.pick_placeholder') },
              ...props.outage.pick_labels.map((label) => ({ value: label, label })),
            ]}
            onChange={(value) => props.onChange(value === '' ? null : value)}
          />
          <Button
            variant="primary"
            size="sm"
            disabled={props.pickedLabel === null || props.picking}
            onClick={() => {
              if (props.pickedLabel !== null) props.onSubmit(props.pickedLabel);
            }}
          >
            {props.picking ? t('key_outage.picking') : t('key_outage.pick_submit')}
          </Button>
        </div>
      </Show>
      <Show when={props.pickError}>
        {(message) => <p class="alert__desc key-outage__pick-error">{message()}</p>}
      </Show>
    </div>
  );
}
